import Head from 'next/head';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import {
  FixtureTickerView,
  useFixtureTickerFilters
} from '@components/fixtures/v2';
import WideLayout from '@components/layout/wide';
import {
  ROLE_LEVELS,
  hasSufficientPrivilegesWithNewUserRestrictions
} from '@lib/utils';
import { useIsAppView } from '@hooks/useIsAppView';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { H1 } from '@components/atoms/typography';
import LoadingView from '@components/atoms/loading-view';
import Paywall from '@components/paywall';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import { getPageSeoHead, PAGE_SEO } from '@lib/page-seo';

const Fixtures = () => {
  const router = useRouter();
  const pathname = router.pathname;
  const pageHead = getPageSeoHead(pathname, PAGE_SEO.fixtureTicker);
  const { isAppView } = useIsAppView();
  const { user: session, isLoading: authIsLoading } = useAuth0User();

  const isPayingMember = useMemo(
    () =>
      hasSufficientPrivilegesWithNewUserRestrictions(session, ROLE_LEVELS.PRO),
    [session]
  );

  const { isError: leagueContextIsError } = useLeagueContext();

  // Only fetch fixtures for users who will actually see the ticker - the
  // paywall branch does not need them. enabled also gates the underlying
  // useLeagueContext / useTeamFixtures queries so unauthed visitors don't
  // trigger background network requests. Whether fixtures actually exist
  // (the off-season question) is decided below from filters.hasFixtures, not
  // from the gameweek number, so the ticker appears on its own the moment the
  // new schedule is published.
  const filters = useFixtureTickerFilters({ enabled: isPayingMember });
  const isPageLoading = authIsLoading || (isPayingMember && filters.isLoading);

  const holdingMessage = (
    <EndOfSeasonHoldingMessage
      pageTitle="The fixture ticker"
      isAppView={isAppView}
    />
  );

  const renderContent = () => {
    if (isPageLoading) {
      return (
        <div className="flex h-[calc(100vh-175px)] justify-center bg-white">
          <LoadingView
            headerText="Loading fixture ticker"
            loadingText="Fetching fixtures..."
            progressBarSeconds={6}
          />
        </div>
      );
    }

    if (leagueContextIsError || filters.isError) {
      return (
        <div className="flex h-64 items-center justify-center text-red-600">
          Could not load fixtures. Please refresh and try again.
        </div>
      );
    }

    if (!isPayingMember) {
      return (
        <div className="p-4 pt-8">
          <Paywall variant="embedded" title="🔒 Unlock the fixture ticker" />
        </div>
      );
    }

    // Off-season: the user pays, but the fixtures endpoint has nothing to show
    // yet (next season's schedule not published). The ticker takes over
    // automatically once hasFixtures flips true.
    if (!filters.hasFixtures) {
      return holdingMessage;
    }

    return <FixtureTickerView {...filters} />;
  };

  return (
    <div className="bg-[#F4F5F7]">
      <Head>{pageHead}</Head>
      <WideLayout content={false}>
        <div className="relative px-4 pt-2 pb-20 sm:px-6 lg:pb-10">
          <div className="relative mx-auto flex max-w-7xl flex-col gap-4 pb-14">
            <div className="flex flex-col gap-1 px-2 pt-4 pb-1 md:flex-row md:items-baseline md:gap-4">
              <H1 data-testid="fixture-ticker-title" className="mb-0">
                Fixtures
              </H1>
              <p className="font-open-sans text-sm text-neutral-600">
                Rank every team&apos;s upcoming run by fixture difficulty,
                expected goals or clean-sheet probability.
              </p>
            </div>
            {renderContent()}
          </div>
        </div>
      </WideLayout>
    </div>
  );
};

export default Fixtures;
